import styles from './EmailTemplatePreview.module.scss';

import { PaletteColor } from '@mui/material';
import { useMemo } from 'react';

import { usePageZoom } from '@work4all/data';

import { EMailTemplate } from '@work4all/models/lib/Classes/EMailTemplate.entity';

import { htmlParser } from '../../../input/format-text/TextEditor/utils/html-parser';
import { colorSchemes } from '../../../theme/color-schemes';
import { PreviewTitle, PreviewWrapper } from '../components';
import { setDefaultHeadStyles } from '../utils';

export type IEmailTemplatePreviewProps = {
  emailTemplate: EMailTemplate;
  onCloseClick?: React.MouseEventHandler<HTMLButtonElement>;
  onEditClicked?: React.MouseEventHandler<HTMLButtonElement>;
  onVisibilityToggle?: (visible: boolean) => void;
};

export function EmailTemplatePreview(props: IEmailTemplatePreviewProps) {
  const { emailTemplate, onCloseClick, onEditClicked, onVisibilityToggle } =
    props;

  const body = useMemo(() => {
    let result = emailTemplate?.body || '';

    // Add signature with divider if signature exists
    if (emailTemplate?.signature?.body) {
      result += htmlParser.asSignature(emailTemplate.signature.body, {
        includeDivider: true,
        useEmailSignature: false,
      });
    }

    return result;
  }, [emailTemplate?.body, emailTemplate.signature?.body]);

  const theme = document.querySelector('html').getAttribute('data-theme');
  // Define inline styles for the signature that will be injected into the iframe
  const signatureStyles = getAdditionalSignatureStyles(
    theme as 'light' | 'dark'
  );

  const { zoomLevel } = usePageZoom();

  return (
    <PreviewWrapper>
      <div className={styles.wrap}>
        <PreviewTitle
          onCloseClick={onCloseClick}
          onEditClicked={onEditClicked}
          onVisibilityToggle={onVisibilityToggle}
        >
          {emailTemplate.name}
        </PreviewTitle>

        <iframe
          className={styles.content}
          title="Email template Preview"
          srcDoc={
            setDefaultHeadStyles({
              value: body || '',
              textmarkStyles: true,
              zoom: zoomLevel,
              additionalStyles: signatureStyles,
            }) || body
          }
        />
      </div>
    </PreviewWrapper>
  );
}

const getAdditionalSignatureStyles = (theme: 'light' | 'dark' = 'light') => {
  const backgroundColor =
    theme === 'dark'
      ? (colorSchemes.dark.palette.ui1 as PaletteColor).main
      : (colorSchemes.light.palette.ui2 as PaletteColor).main;
  const textColor =
    theme === 'dark'
      ? colorSchemes.dark.palette.text.tertiary
      : colorSchemes.light.palette.text.tertiary;
  const lineColor =
    theme === 'dark'
      ? (colorSchemes.dark.palette.ui4 as PaletteColor).main
      : (colorSchemes.light.palette.ui4 as PaletteColor).main;

  return `
    .signature {
      opacity: 0.5;
      margin-top: 1rem;
      position: relative;
      cursor: default;
    }

    .signature-content {
      margin-top: 0.75rem;
      pointer-events: auto;
      user-select: text;
    }

    .signature-divider {
      position: relative;
      margin-bottom: 1.25rem;
      height: 20px;
    }

    .signature-label {
      display: inline-block;
      padding-right: 8px;
      z-index: 2;
      position: relative;
      background-color: ${backgroundColor};
      color: ${textColor};
      font-size: 0.85rem;
      line-height: 1rem;
      letter-spacing: 0.4px;
    }

    .signature-line {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      background: ${lineColor};
      border: none;
      margin: 0;
      z-index: 1;
    }
  `;
};
